import React, { useCallback } from 'react';
import { styled } from '@compiled/react';

import { Inline, xcss, Anchor } from '@atlaskit/primitives';
import TeamsIcon from '@atlaskit/icon/core/teams';
import { Text } from '@atlaskit/primitives/compiled';
import { useGlobalObjectPreviewPanels } from '@atlassian/jira-preview-panels/src/PreviewPanelsProvider.tsx';
import { useIsModal } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { TeamReadViewWithGppProps } from './types.tsx';
import messages from './messages.tsx';

/**
 * The TeamReadViewWithGPP will show a read only view of Team field, which can open up GPP
 * @param props [TeamReadViewProps](./types.tsx)
 */
export const TeamReadViewWithGpp = ({
	teamName,
	teamId,
	ariId,
	iconUrl,
	maxLines,
}: TeamReadViewWithGppProps) => {
	const { openPreviewPanel, isPreviewPanelAvailable } = useGlobalObjectPreviewPanels();
	const { formatMessage } = useIntl();
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const teamUrl = `${window.location.origin}/jira/people/team/${teamId}`;

	const openPreviewPanelWithTeamData = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			// For keyboard events, only respond to Enter and Space
			if ('key' in event && event.key !== 'Enter' && event.key !== ' ') {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			openPreviewPanel({
				ari: ariId,
				url: teamUrl,
				name: 'identity-team',
			});
		},
		[ariId, openPreviewPanel, teamUrl],
	);

	const handleLinkClick = useCallback((event: React.MouseEvent | React.KeyboardEvent) => {
		event.stopPropagation();
	}, []);

	const ariaLabel = formatMessage(messages.teamPreviewLabel, { teamName });

	const isModal = useIsModal();
	const canShowPreview = !isModal && isPreviewPanelAvailable({ ari: ariId });

	if (!ariId || !teamId) {
		return <Text maxLines={maxLines}>{teamName}</Text>;
	}

	return (
		<Inline alignBlock="center" space="space.100" xcss={containerStyles}>
			{iconUrl ? <Icon src={iconUrl} alt="" /> : <TeamsIcon label="" size="small" />}
			{canShowPreview ? (
				<TeamName
					onClick={openPreviewPanelWithTeamData}
					onKeyDown={openPreviewPanelWithTeamData}
					role="button"
					tabIndex={0}
					aria-label={ariaLabel}
				>
					{teamName}
				</TeamName>
			) : (
				<Anchor href={teamUrl} target="_blank" onClick={handleLinkClick} xcss={teamLinkStyles}>
					{teamName}
				</Anchor>
			)}
		</Inline>
	);
};

const containerStyles = xcss({
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	paddingInline: 'space.075',
	paddingBlock: 'space.050',
});

const teamLinkStyles = xcss({
	cursor: 'pointer',
	textDecoration: 'none',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	color: 'color.text',
	':hover': {
		textDecoration: 'underline',
		color: 'color.text',
	},
	':visited': {
		color: 'color.text',
	},
	':focus': {
		color: 'color.text',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Icon = styled.img({
	maxWidth: '16px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TeamName = styled.span({
	cursor: 'pointer',
	textDecoration: 'none',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	'&:hover': {
		textDecoration: 'underline',
	},
});
